import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Bell, X, Building2, Megaphone, Sparkles, Pin, Inbox } from 'lucide-react';


const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const LS_KEY = 'cr_updates_last_seen';

/* ── Type config ── */
const TYPE_CONFIG = {
  college_added: { Icon: Building2, label: 'College Added', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  announcement:  { Icon: Megaphone, label: 'Announcement',  color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  feature:       { Icon: Sparkles,  label: 'New Feature',   color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
  general:       { Icon: Pin,       label: 'Update',        color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

/* ── Relative time ── */
function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)     return 'just now';
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ── Update Card ── */
const UpdateCard = ({ update, isNew }) => {
  const cfg = TYPE_CONFIG[update.type] || TYPE_CONFIG.general;
  const { Icon } = cfg;

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${isNew ? cfg.border : '#f0f0f0'}`,
      borderRadius: 16,
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      position: 'relative',
      boxShadow: isNew ? `0 0 0 3px ${cfg.border}` : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.2s',
    }}>
      {/* NEW indicator */}
      {isNew && (
        <div style={{
          position: 'absolute',
          top: -1, left: 20,
          background: cfg.color,
          color: '#fff',
          fontSize: 9,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.08em',
          padding: '2px 8px',
          borderRadius: '0 0 6px 6px',
          textTransform: 'uppercase',
        }}>NEW</div>
      )}

      {/* Header: type badge + time */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: isNew ? 8 : 0 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: cfg.bg, color: cfg.color,
          border: `1px solid ${cfg.border}`,
          borderRadius: 9999, padding: '4px 12px',
          fontSize: 11, fontFamily: 'var(--font-mono)',
          fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          <Icon size={11} strokeWidth={2.5} />
          {cfg.label}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11,
          color: '#9ca3af', letterSpacing: '0.02em',
        }}>
          {timeAgo(update.createdAt)}
        </span>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: 'var(--font-primary)',
        fontWeight: 700, fontSize: 16,
        color: '#111', margin: 0, lineHeight: 1.35,
      }}>
        {update.title}
      </h3>

      {/* Content */}
      <p style={{
        fontFamily: 'var(--font-primary)',
        fontSize: 14, color: '#4b5563',
        lineHeight: 1.65, margin: 0,
      }}>
        {update.content}
      </p>
    </div>
  );
};

/* ── Skeleton ── */
const UpdateSkeleton = ({ delay = 0 }) => (
  <div style={{
    background: '#fff', border: '1px solid #f0f0f0',
    borderRadius: 16, padding: '20px 22px',
    display: 'flex', flexDirection: 'column', gap: 10,
    animationDelay: `${delay}s`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div className="skeleton" style={{ height: 24, width: 130, borderRadius: 9999 }} />
      <div className="skeleton" style={{ height: 14, width: 50, borderRadius: 4 }} />
    </div>
    <div className="skeleton" style={{ height: 18, width: '75%', borderRadius: 6 }} />
    <div className="skeleton" style={{ height: 14, width: '100%', borderRadius: 4 }} />
    <div className="skeleton" style={{ height: 14, width: '65%', borderRadius: 4 }} />
  </div>
);

/* ══════════════════════════════
   MAIN COMPONENT
══════════════════════════════ */
const UpdatesPanel = () => {
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSeen, setLastSeen] = useState(() =>
    parseInt(localStorage.getItem(LS_KEY) || '0', 10)
  );

  const fetchUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/updates`);
      setUpdates(res.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  useEffect(() => {
    if (open) {
      const now = Date.now();
      localStorage.setItem(LS_KEY, String(now));
      setLastSeen(now);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const unreadCount = updates.filter(
    u => new Date(u.createdAt).getTime() > lastSeen
  ).length;

  return (
    <>
      {/* ── Bell trigger button ── */}
      <button
        id="updates-bell-btn"
        onClick={() => setOpen(true)}
        title="What's New"
        style={{
          position: 'relative',
          background: 'none',
          border: '1.5px solid #e5e7eb',
          borderRadius: 10,
          width: 38, height: 38,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0,
          color: '#374151',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = '#111';
          e.currentTarget.style.borderColor = '#111';
          e.currentTarget.style.color = '#fff';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.color = '#374151';
        }}
      >
        <Bell size={16} strokeWidth={2} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -6, right: -6,
            background: '#2563eb', color: '#fff',
            borderRadius: 9999, minWidth: 18, height: 18,
            fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 5px', border: '2px solid #fff',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Drawer & Backdrop (rendered via Portal) ── */}
      {ReactDOM.createPortal(
        <>
          {/* ── Backdrop ── */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              zIndex: 9999,
              opacity: open ? 1 : 0,
              pointerEvents: open ? 'auto' : 'none',
              transition: 'opacity 0.25s ease',
            }}
          />

          {/* ── Drawer ── */}
          <div style={{
            position: 'fixed',
            top: 0, right: 0, bottom: 0,
            width: 'min(480px, 100vw)',
            background: '#f8f9fa',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            transform: open ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
            boxShadow: '-20px 0 60px rgba(0,0,0,0.15)',
          }}>

        {/* ── Header ── */}
        <div style={{
          padding: '28px 28px 20px',
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{
                fontFamily: 'var(--font-primary)',
                fontWeight: 800, fontSize: 24,
                color: '#111', margin: 0,
                letterSpacing: '-0.03em',
              }}>
                What's New
              </h2>
              <p style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12, color: '#9ca3af',
                margin: '6px 0 0',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}>
                {loading
                  ? 'Loading…'
                  : updates.length === 0
                    ? 'No updates yet'
                    : `${updates.length} update${updates.length !== 1 ? 's' : ''}${unreadCount > 0 ? ` · ${unreadCount} new` : ''}`
                }
              </p>
            </div>

            <button
              onClick={() => setOpen(false)}
              aria-label="Close updates panel"
              style={{
                background: '#f3f4f6', border: 'none',
                borderRadius: 9999, width: 36, height: 36,
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: '#6b7280', flexShrink: 0,
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#111'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* ── Scrollable list ── */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <UpdateSkeleton key={i} delay={i * 0.08} />
              ))
            : updates.length === 0
              ? (
                <div style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: '80px 24px', textAlign: 'center',
                }}>
                  <div style={{
                    width: 72, height: 72, borderRadius: 20,
                    background: '#f3f4f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 20,
                  }}>
                    <Inbox size={32} strokeWidth={1.5} color="#9ca3af" />
                  </div>
                  <h3 style={{
                    fontFamily: 'var(--font-primary)', fontWeight: 700,
                    fontSize: 18, color: '#111', marginBottom: 10,
                  }}>
                    Nothing here yet
                  </h3>
                  <p style={{
                    fontFamily: 'var(--font-primary)', color: '#6b7280',
                    fontSize: 14, lineHeight: 1.6, maxWidth: 260,
                  }}>
                    We'll post college additions, new features, and announcements here.
                  </p>
                </div>
              )
              : updates.map(update => (
                  <UpdateCard
                    key={update._id}
                    update={update}
                    isNew={new Date(update.createdAt).getTime() > lastSeen}
                  />
                ))
          }
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#22c55e',
            boxShadow: '0 0 0 2px rgba(34,197,94,0.2)',
          }} />
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            color: '#9ca3af', margin: 0,
            letterSpacing: '0.04em',
          }}>
            Posted by CampusReveal · 100% student-first
          </p>
        </div>
      </div>
    </>,
    document.body
  )}
    </>
  );
};

export default UpdatesPanel;
